import { ModuleShell } from '../components/ModuleShell';
import { DataTable, type Column } from '../components/DataTable';
import { Dot, Mono, StatusChip } from '../components/StatusChip';
import { timeAgo, whColor } from '../api/format';
import type { SystemHealthResponse, CollectorHealth, WebhookHealth, ModuleState } from '../api/types';

function healthVariant(c: CollectorHealth): 'success' | 'warning' | 'error' | 'stale' {
  if (c.consecutive_failures) return 'error';
  if (c.health_warning) return 'warning';
  if (c.freshness === 'stale') return 'stale';
  return 'success';
}

interface Props {
  moduleState: ModuleState<SystemHealthResponse> & { refetch: () => void };
  shadowUnresolved?: number;
  missiveIncomplete?: boolean;
}

export function SystemModule({ moduleState, shadowUnresolved = 0, missiveIncomplete = false }: Props) {
  const data = moduleState.data;
  const collectors = data?.collectors || [];
  const webhooks = data?.webhook_health || [];

  const failingCount = collectors.filter(c => c.consecutive_failures).length;
  const staleCount = collectors.filter(c => c.freshness === 'stale').length;
  const highErrorRate = collectors.filter(c => c.error_rate_24h > 0.5).length;

  const whCols: Column<WebhookHealth>[] = [
    { key: 'client', label: 'Client', width: '150px', sortValue: r => r.client_name, render: r => <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>{r.client_name}</span> },
    { key: 'campaigns', label: 'Campaigns', align: 'right', sortValue: r => r.active_campaigns, render: r => <Mono color="var(--text-muted)">{r.active_campaigns}</Mono> },
    { key: 'sends', label: 'Sends', align: 'right', sortValue: r => r.recent_sends, render: r => <Mono color={r.recent_sends > 0 ? 'var(--text-primary)' : 'var(--text-dim)'}>{r.recent_sends}</Mono> },
    { key: 'events', label: 'Events 24h', align: 'right', sortValue: r => r.webhook_events_24h, render: r => <Mono color="var(--text-muted)">{r.webhook_events_24h}</Mono> },
    { key: 'status', label: 'Status', align: 'right', width: '100px', sortValue: r => r.webhook_status, render: r => <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 5 }}><Dot color={whColor(r.webhook_status)} size={5} /><span style={{ fontSize: 10, color: whColor(r.webhook_status), textTransform: 'uppercase', fontWeight: 600 }}>{r.webhook_status.replace('_', ' ')}</span></div> },
  ];

  return (
    <ModuleShell name="system" title="System Health" status={moduleState.status} error={moduleState.error} dataFreshness={null} fetchedAt={moduleState.fetchedAt} isStale={moduleState.isStale} truth={moduleState.truth} staleReason={moduleState.staleReason} onRetry={moduleState.refetch}
      headerRight={<Mono color="var(--text-dim)" style={{ fontSize: 11 }}>v{data?.version || '?'}</Mono>}>

      {/* System-level truth warnings */}
      {(failingCount > 0 || staleCount > 0 || highErrorRate > 0 || shadowUnresolved > 0 || missiveIncomplete) && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 16, padding: '10px 16px', background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', fontSize: 11, color: 'var(--text-ghost)', flexWrap: 'wrap', alignItems: 'center' }}>
          {failingCount > 0 && <span><StatusChip label={`${failingCount} failing`} variant="error" showIcon /> collectors have consecutive failures</span>}
          {staleCount > 0 && <span><StatusChip label={`${staleCount} stale`} variant="stale" showIcon /> collectors haven't synced recently</span>}
          {highErrorRate > 0 && <span><StatusChip label={`${highErrorRate} high error rate`} variant="warning" showIcon /> collectors above 50% error rate</span>}
          {shadowUnresolved > 0 && <span><StatusChip label={`${shadowUnresolved} shadow`} variant="proxy" showIcon /> unresolved Missive conversations</span>}
          {missiveIncomplete && <span><StatusChip label="incomplete" variant="stale" showIcon /> Missive data may be truncated</span>}
        </div>
      )}

      <div style={{ marginBottom: 32 }}>
        <h3 style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>Collectors</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 }}>
          {collectors.map(c => {
            const v = healthVariant(c);
            const col = v === 'error' ? 'var(--red)' : v === 'warning' ? 'var(--yellow)' : v === 'stale' ? 'var(--orange)' : 'var(--green)';
            return (
              <div key={c.collector} style={{ padding: '14px 16px', background: 'var(--bg-surface)', border: `1px solid ${col}25`, borderRadius: 'var(--radius-md)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', letterSpacing: '0.04em' }}>{c.collector.replace('sync_', '').replace(/_/g, ' ').toUpperCase()}</span>
                  <StatusChip label={v} variant={v} dot />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 8 }}>
                  {[
                    { l: 'LAST SYNC', v: timeAgo(c.last_completed) },
                    { l: 'FRESHNESS', v: c.freshness, c: c.freshness === 'ok' ? 'var(--green)' : 'var(--yellow)' },
                    { l: '24H RUNS', v: String(c.runs_24h) },
                    { l: 'ERR RATE', v: (c.error_rate_24h * 100).toFixed(0) + '%', c: c.error_rate_24h > 0.5 ? 'var(--red)' : c.error_rate_24h > 0 ? 'var(--yellow)' : 'var(--green)' },
                  ].map(x => <div key={x.l}><div style={{ fontSize: 9, color: 'var(--text-ghost)', letterSpacing: '0.06em', marginBottom: 2 }}>{x.l}</div><Mono color={x.c || 'var(--text-secondary)'} style={{ fontSize: 12 }}>{x.v}</Mono></div>)}
                </div>
                {c.consecutive_failures && <div style={{ marginTop: 8, fontSize: 10, color: 'var(--red)', fontWeight: 600 }}>⚠ Consecutive failures</div>}
              </div>
            );
          })}
        </div>
      </div>
      <h3 style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>Webhook Health · {webhooks.length} workspaces</h3>
      <DataTable columns={whCols} rows={webhooks} rowKey={r => r.workspace_id} />
    </ModuleShell>
  );
}
